import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  useDefaultModelQuery,
  useModelQuery,
} from '__queries/models/custom-objects';
import {
  GetFilterVariableOpts,
  getFilterVariables,
} from '@kizen/filters/variables';
import { getBusinessClientObject } from 'store/authentication/selectors';

export type UseFilterVariableOptions = Pick<
  GetFilterVariableOpts,
  'fieldSettingsSearch' | 'variableValues' | 'variableSources' | 'instance'
> & {
  objectId?: string | null;
  noObjectContext?: boolean;
};

/**
 * When called with no arguments, will return variables for contact filters. To create variables
 * for a custom object, supply an `options.objectId` value.
 */
export const useFilterVariables = (options?: UseFilterVariableOptions) => {
  const isClient = !options?.objectId && !options?.noObjectContext;
  const access = useSelector((s: any) => s.authentication.access);
  const businessClientData = useSelector(getBusinessClientObject);
  const objectQuery = useModelQuery(
    options?.objectId,
    !isClient && Boolean(options?.objectId)
  );
  const contactQuery = useDefaultModelQuery(businessClientData.id, {
    enabled: isClient && Boolean(businessClientData.access),
  });
  const objectModel = objectQuery.data;
  const contactModel = contactQuery.data;
  const isLoading = objectQuery.isLoading || contactQuery.isLoading;
  const { fieldSettingsSearch, variableValues, variableSources, instance } =
    options ?? {};

  const variables = useMemo(() => {
    const opts: GetFilterVariableOpts = { access };
    const model = isClient ? contactModel : objectModel;

    if (!isClient && contactModel) {
      opts.clientTagId = contactModel.undeletableFields.tags.id;
    }

    if (fieldSettingsSearch) {
      opts.fieldSettingsSearch = true;
    }

    if (variableValues) {
      opts.variableValues = variableValues;
    }

    if (variableSources) {
      opts.variableSources = variableSources;
    }

    if (instance) {
      opts.instance = instance;
    }

    return getFilterVariables(model, opts);
  }, [
    contactModel,
    objectModel,
    isClient,
    access,
    fieldSettingsSearch,
    variableValues,
    variableSources,
    instance,
  ]);

  return { variables, isLoading };
};
